import { IAccount } from "@/types";
import { ExtendedMoveTaskParams, ExtendedTask, ExtendedTaskList, OptimisticUpdatedLists } from "@/components/Task/types";
import queryClient from "@/query-client";
import { DropResult } from "react-beautiful-dnd";
import { useTaskSortStore } from "./stores/useTaskSortStore";

export const convertDateToISOString = (jsDate: Date): string => {
  // Get the local date components
  const year = jsDate.getFullYear();
  const month = jsDate.getMonth() + 1; // JavaScript months are 0-based
  const day = jsDate.getDate();

  // Create a UTC date string with the local date components
  const utcDate = new Date(Date.UTC(year, month - 1, day));

  // Return the ISO string, ensuring to set time to 00:00:00.000Z
  return utcDate.toISOString().split('T')[0] + 'T00:00:00.000Z';
};

export const getAccountDisplayName = (account: IAccount, nickname: string | undefined, maxLength: number | null = null) => {
  const displayName = nickname || account.name;
  if (!maxLength) return displayName;
  return displayName.length > maxLength ? truncateString(displayName, maxLength) : displayName;
}

export const truncateString = (str: string, maxLength: number) => {
  return str.length > maxLength ? `${str.substring(0, maxLength)}...` : str;
}

/**
 * Updates the position properties of tasks based on their order in the array
 * 
 * @param tasks - Array of ExtendedTask items in the desired order
 * @returns A new array with the same tasks but with updated position properties
 */
export const updateTaskPositions = (tasks: ExtendedTask[]): ExtendedTask[] => {
  // Create a new array to avoid mutating the original
  return tasks.map((task, index) => {
    // Generate a position string with leading zeros (e.g., "0000000001", "0000000002")
    // Google Tasks API uses 10-digit strings for positions
    const position = index.toString().padStart(10, '0');
    return { ...task, position };
  });
};

/**
 * Parameters for moving a task according to Google Tasks API
 */
interface MoveTaskParams {
  /**
   * ID of the task that should be before the moved task.
   * If omitted, the task is moved to the first position among its siblings.
   */
  previous?: string;
  
  /**
   * ID of the parent task for the moved task.
   * If omitted, the task is moved to the top level.
   */
  parent?: string;
  
  /**
   * ID of the destination tasklist (not used for sorting).
   */
  destinationTasklist?: string;
}

/**
 * Sorts a list of tasks based on Google Tasks API move parameters
 * 
 * @param tasks - Array of ExtendedTask items to be sorted
 * @param taskId - ID of the task to be moved
 * @param moveParams - Parameters for moving the task (previous, parent, destinationTasklist)
 * @returns A new array with tasks in the correct order
 */
/////////////// TODO important - check if this is correct (LLM generated logic)
export const sortTasksByMoveParams = (
  tasks: ExtendedTask[],
  taskId: string,
  moveParams: MoveTaskParams
): ExtendedTask[] => {
  const { previous, parent, } = moveParams;
  
  // Create a copy of the tasks array
  const sortedTasks = [...tasks];
  
  // Find the task to be moved
  const taskIndex = sortedTasks.findIndex(task => task.id === taskId);
  
  if (taskIndex === -1) {
    return sortedTasks; // Task not found, return unchanged
  }
  
  // Remove the task from its current position
  const [taskToMove] = sortedTasks.splice(taskIndex, 1);
  
  // Find where to insert the task
  let insertIndex = 0;
  
  if (previous) {
    // Find the index of the previous task
    const previousIndex = sortedTasks.findIndex(task => task.id === previous);
    
    // Insert after the previous task if found
    if (previousIndex !== -1) {
      insertIndex = previousIndex + 1;
    }
  } else if (parent) {
    // If no previous but parent is specified, insert as the first child of parent
    // Find the first task with this parent
    const firstChildIndex = sortedTasks.findIndex(task => task.parent === parent);
    
    if (firstChildIndex !== -1) {
      // Insert at the beginning of the siblings
      insertIndex = firstChildIndex;
    } else {
      // If no siblings yet, find the parent and insert right after it
      const parentIndex = sortedTasks.findIndex(task => task.id === parent);
      if (parentIndex !== -1) {
        insertIndex = parentIndex + 1;
      }
    }
  }
  // If neither previous nor parent is specified, insertIndex remains 0
  // which places the task at the beginning of the list (top level)
  
  // Insert the task at the calculated position
  sortedTasks.splice(insertIndex, 0, taskToMove);
  
  return sortedTasks;
};

export const reorderListWithStartAndEndIndexes = (list: ExtendedTask[], startIndex: number, endIndex: number) => {
  const result = Array.from(list);
  // First remove the item from its original position
  const [removed] = result.splice(startIndex, 1);
  // Then insert it at the destination index in the modified array
  result.splice(endIndex, 0, removed);
  return result;
};

export const getPreviousTask = (list: ExtendedTask[], taskId: string) => {
  const index = list.findIndex(task => task.id === taskId);
  return list[index - 1];
};

export const getAccountFromListId = (listId: string) => {
  const accounts = queryClient.getQueryData<Record<string, IAccount>>(["accounts"]);
  if (!accounts) return null;
  const accountIds = Object.keys(accounts);
  for (const accountId of accountIds) {
    const tasklists = queryClient.getQueryData<ExtendedTaskList[]>(["tasklists", { userId: accountId }]);
    if (!tasklists) continue;
    for (const list of tasklists) {
      if (list.id === listId) {
        return accountId;
      }
    }
  }
  return null;
}

export const makeDroppableId = (accountId: string, listId: string) => {
  return `${accountId}-${listId}`;
}

export const parseDroppableId = (droppableId: string) => {
  const [accountId, listId] = droppableId.split("-");
  return { accountId, listId };
}

export const makeDraggableId = (accountId: string, listId: string, taskId: string) => {
  return `${accountId}-${listId}-${taskId}`;
}

export const parseDraggableId = (draggableId: string) => {
  const [accountId, listId, taskId] = draggableId.split("-");
  return { accountId, listId, taskId };
}

export const moveTaskInSameList = (result: DropResult, cb: (moveTaskParams: ExtendedMoveTaskParams, optimisticUpdatedLists?: OptimisticUpdatedLists) => void) => {
  const { destination, source } = result;
  if (!destination) return;

  const { accountId: sourceAccountId, listId: sourceListId } = parseDroppableId(source.droppableId);
  const { taskId } = parseDraggableId(result.draggableId);

  const list = useTaskSortStore.getState().getTasksOrders(sourceAccountId, sourceListId)
  const setTasksOrders = useTaskSortStore.getState().setTasksOrders;
  const pendingList = list.filter(task => task.status === "needsAction")

  let destinationIndex = result.destination?.index;
  if (destinationIndex == undefined) return;
  const listToReorder = pendingList;
  const reorderedList = reorderListWithStartAndEndIndexes(listToReorder, result.source.index, destinationIndex);
  const account = queryClient.getQueryData<Record<string, IAccount>>(["accounts"])?.[sourceAccountId];
  if (!account) return;

  const moveTaskParams: ExtendedMoveTaskParams = {
    account,
    tasklist: sourceListId,
    taskId,
  }

  const previousId = getPreviousTask(reorderedList, taskId)?.id;
  moveTaskParams.previous = previousId || undefined;
  setTasksOrders(sourceAccountId, sourceListId, reorderedList);
  cb(moveTaskParams);
}

export const moveTaskInDifferentList = (result: DropResult, cb: (moveTaskParams: ExtendedMoveTaskParams, optimisticUpdatedLists?: OptimisticUpdatedLists) => void) => {
  const { destination, source } = result;
  if (!destination) return;
  
  const { accountId: sourceAccountId, listId: sourceListId } = parseDroppableId(source.droppableId);
  const { accountId: destinationAccountId, listId: destinationListId } = parseDroppableId(destination.droppableId);
  const { getTasksOrders } = useTaskSortStore.getState();
  const sourceList = getTasksOrders(sourceAccountId, sourceListId);
  const destinationList = getTasksOrders(destinationAccountId, destinationListId);
  const taskId = parseDraggableId(result.draggableId).taskId;
  const task = sourceList.find(task => task.id === taskId);
  if (!task) return;
  
  const updatedSourceList = sourceList.filter(task => task.id !== taskId);
  const updatedDestinationList = [...destinationList]
  updatedDestinationList.splice(destination.index, 0, task);
  const account = queryClient.getQueryData<Record<string, IAccount>>(["accounts"])?.[sourceAccountId];
  if (!account) return;

  const moveTaskParams: ExtendedMoveTaskParams = {
    account,
    tasklist: sourceListId,
    destinationTasklist: destinationListId,
    taskId,
  }

  const previousId = getPreviousTask(updatedDestinationList, taskId)?.id;
  moveTaskParams.previous = previousId || undefined;
  cb(moveTaskParams, { updatedSourceList, updatedDestinationList });
}